import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Globe, Bell, Clock, RotateCcw, Plus, ExternalLink, AlertTriangle, Pause, Play, Trash2, Settings, Info, X } from 'lucide-react';
import { useStore } from '@/store/useStore';
import { t, getLocalizedString } from '@/i18n/translations';
import { Language, Habit, HabitCustomization, HabitTemplate } from '@/types';
import HabitEditModal from '@/components/HabitEditModal';
import { requestNotificationPermission, sendTestNotification } from '@/hooks/useNotifications';

const LANGUAGES: { code: Language; label: string }[] = [
  { code: 'nl', label: 'Nederlands' },
  { code: 'fr', label: 'Français' },
  { code: 'en', label: 'English' },
];

export default function SettingsPage() {
  const navigate = useNavigate();
  const { settings, progress, templates, setLanguage, setNotifications, setReminderTime, removeTemplate, resetProgress, updateHabitCustomization } = useStore();
  const lang = settings.language;

  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [resetConfirmText, setResetConfirmText] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<{ templateId: string; templateName: string } | null>(null);
  const [editingHabit, setEditingHabit] = useState<Habit | null>(null);
  const [infoTemplate, setInfoTemplate] = useState<HabitTemplate | null>(null);
  const [infoHabit, setInfoHabit] = useState<Habit | null>(null);

  const selectedTemplates = templates.filter((t) => (progress.selectedTemplates || []).includes(t.id));

  // Get templates with their habits and pause status
  const templatesWithStatus = useMemo(() => {
    const customHabits = progress.customHabits || {};
    return selectedTemplates.map((template) => {
      const habitsWithStatus = template.habits.map((habit) => {
        const custom = customHabits[habit.id];
        return {
          habit,
          isPaused: custom?.disabled || false,
        };
      });
      const allPaused = habitsWithStatus.every((h) => h.isPaused);
      const allActive = habitsWithStatus.every((h) => !h.isPaused);
      return {
        template,
        habits: habitsWithStatus,
        allPaused,
        allActive,
        isPartiallyPaused: !allPaused && !allActive,
      };
    });
  }, [selectedTemplates, progress.customHabits]);

  const handlePauseTemplate = (templateId: string) => {
    const template = selectedTemplates.find((t) => t.id === templateId);
    if (template) {
      template.habits.forEach((habit) => {
        updateHabitCustomization(habit.id, { disabled: true });
      });
    }
  };

  const handleUnpauseTemplate = (templateId: string) => {
    const template = selectedTemplates.find((t) => t.id === templateId);
    if (template) {
      template.habits.forEach((habit) => {
        updateHabitCustomization(habit.id, { disabled: false });
      });
    }
  };

  const handleSaveHabitCustomization = (customization: HabitCustomization) => {
    if (editingHabit) {
      updateHabitCustomization(editingHabit.id, customization);
    }
  };

  const handleReset = () => {
    if (resetConfirmText === 'RESET') {
      resetProgress();
      setShowResetConfirm(false);
      setResetConfirmText('');
      navigate('/');
    }
  };

  const handlePauseHabit = (habitId: string) => {
    updateHabitCustomization(habitId, { disabled: true });
  };

  const handleUnpauseHabit = (habitId: string) => {
    updateHabitCustomization(habitId, { disabled: false });
  };

  // Check if all habits in a template are paused (can delete)
  const canDeleteTemplate = (templateId: string) => {
    const template = selectedTemplates.find((t) => t.id === templateId);
    if (!template) return false;
    const customHabits = progress.customHabits || {};
    return template.habits.every((habit) => customHabits[habit.id]?.disabled);
  };

  return (
    <div className="min-h-screen pb-20">
      {/* Settings List */}
      <div className="px-4 py-6 space-y-4">
        {/* Language */}
        <SettingsCard>
          <div className="flex items-center gap-3 mb-3">
            <Globe className="w-5 h-5 text-gray-500" />
            <span className="font-medium text-gray-800">{t('settings.language', lang)}</span>
          </div>
          <div className="flex gap-2">
            {LANGUAGES.map((language) => (
              <button
                key={language.code}
                onClick={() => setLanguage(language.code)}
                className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
                  settings.language === language.code
                    ? 'bg-primary-500 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {language.label}
              </button>
            ))}
          </div>
        </SettingsCard>

        {/* Notifications */}
        <SettingsCard>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Bell className="w-5 h-5 text-gray-500" />
              <div>
                <span className="font-medium text-gray-800">{t('settings.notifications', lang)}</span>
                {'Notification' in window && Notification.permission === 'denied' && (
                  <p className="text-xs text-red-500 mt-0.5">
                    {lang === 'nl' ? 'Geblokkeerd in browser' : lang === 'fr' ? 'Bloqué dans le navigateur' : 'Blocked in browser'}
                  </p>
                )}
              </div>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={settings.notifications}
                onChange={async (e) => {
                  if (e.target.checked) {
                    const granted = await requestNotificationPermission();
                    if (granted) {
                      setNotifications(true);
                    }
                  } else {
                    setNotifications(false);
                  }
                }}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-100 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-500"></div>
            </label>
          </div>
        </SettingsCard>

        {/* Reminder Time & Test */}
        {settings.notifications && (
          <SettingsCard>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                <Clock className="w-5 h-5 text-gray-500" />
                <span className="font-medium text-gray-800">{t('settings.reminderTime', lang)}</span>
              </div>
              <input
                type="time"
                value={settings.reminderTime}
                onChange={(e) => setReminderTime(e.target.value)}
                className="bg-gray-100 rounded-lg px-3 py-2 text-gray-700"
              />
            </div>
            <button
              onClick={sendTestNotification}
              className="w-full py-2 text-sm text-primary-600 bg-primary-50 hover:bg-primary-100 rounded-lg transition-colors"
            >
              {lang === 'nl' ? '🔔 Test notificatie' : lang === 'fr' ? '🔔 Tester notification' : '🔔 Test notification'}
            </button>
          </SettingsCard>
        )}

        {/* Your Habits - Grouped by Template */}
        <SettingsCard>
          <div className="flex items-center justify-between mb-4">
            <span className="font-medium text-gray-800">{t('settings.yourHabits', lang)}</span>
            <button
              onClick={() => navigate('/onboarding')}
              className="flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 font-medium"
            >
              <Plus className="w-4 h-4" />
              {t('settings.addHabit', lang)}
            </button>
          </div>

          {templatesWithStatus.length === 0 ? (
            <p className="text-sm text-gray-500">{t('settings.noHabits', lang)}</p>
          ) : (
            <div className="space-y-4">
              {templatesWithStatus.map(({ template, habits, allPaused, isPartiallyPaused }) => (
                <div
                  key={template.id}
                  className={`rounded-xl border ${allPaused ? 'border-gray-200 bg-gray-50/50' : 'border-gray-200 bg-white'}`}
                >
                  {/* Template Header */}
                  <div className={`flex items-start justify-between p-3 border-b ${allPaused ? 'border-gray-100' : 'border-gray-100'}`}>
                    <div className="flex items-start gap-3">
                      <span className="text-2xl">{template.icon}</span>
                      <div>
                        <div className="flex items-center gap-1.5">
                          <h4 className={`font-medium ${allPaused ? 'text-gray-500' : 'text-gray-800'}`}>
                            {getLocalizedString(template.name, lang)}
                          </h4>
                          <button
                            onClick={() => setInfoTemplate(template)}
                            className="p-0.5 rounded text-gray-400 hover:text-primary-500 transition-colors"
                            title={lang === 'nl' ? 'Meer info' : lang === 'fr' ? 'Plus d\'info' : 'More info'}
                          >
                            <Info className="w-4 h-4" />
                          </button>
                        </div>
                        <p className="text-xs text-gray-400 mt-0.5">
                          {getLocalizedString(template.description, lang)}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {allPaused ? (
                        <>
                          <button
                            onClick={() => handleUnpauseTemplate(template.id)}
                            className="p-1.5 text-primary-600 hover:text-primary-700 hover:bg-primary-50 rounded-lg transition-colors"
                            title={lang === 'nl' ? 'Alles hervatten' : lang === 'fr' ? 'Tout reprendre' : 'Resume all'}
                          >
                            <Play className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => setShowDeleteConfirm({ templateId: template.id, templateName: getLocalizedString(template.name, lang) })}
                            className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            title={lang === 'nl' ? 'Verwijderen' : lang === 'fr' ? 'Supprimer' : 'Delete'}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </>
                      ) : (
                        <button
                          onClick={() => handlePauseTemplate(template.id)}
                          className="flex items-center gap-1 text-xs text-amber-600 hover:text-amber-700 font-medium px-2 py-1 rounded-lg hover:bg-amber-50 transition-colors"
                          title={lang === 'nl' ? 'Alles pauzeren' : lang === 'fr' ? 'Tout mettre en pause' : 'Pause all'}
                        >
                          <Pause className="w-3.5 h-3.5" />
                          {lang === 'nl' ? 'Pauze' : lang === 'fr' ? 'Pause' : 'Pause'}
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Habits in this template */}
                  <div className="p-2 space-y-1">
                    {habits.map(({ habit, isPaused }) => (
                      <div
                        key={habit.id}
                        className={`flex items-center justify-between rounded-lg px-2 py-1.5 ${
                          isPaused ? 'opacity-50' : ''
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <span className="text-base">{habit.icon}</span>
                          <span className={`text-sm ${isPaused ? 'text-gray-400' : 'text-gray-600'}`}>
                            {getLocalizedString(habit.name, lang)}
                          </span>
                          <button
                            onClick={() => setInfoHabit(habit)}
                            className="p-0.5 rounded text-gray-300 hover:text-primary-500 transition-colors"
                            title={lang === 'nl' ? 'Meer info' : lang === 'fr' ? 'Plus d\'info' : 'More info'}
                          >
                            <Info className="w-3.5 h-3.5" />
                          </button>
                          {isPaused && (
                            <span className="text-xs text-amber-500 bg-amber-50 px-1.5 py-0.5 rounded">
                              {lang === 'nl' ? 'pauze' : lang === 'fr' ? 'pause' : 'paused'}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => setEditingHabit(habit)}
                            className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
                            title={lang === 'nl' ? 'Aanpassen' : lang === 'fr' ? 'Modifier' : 'Edit'}
                          >
                            <Settings className="w-3.5 h-3.5" />
                          </button>
                          {isPaused ? (
                            <button
                              onClick={() => handleUnpauseHabit(habit.id)}
                              className="p-1 text-primary-500 hover:text-primary-600 transition-colors"
                              title={lang === 'nl' ? 'Hervatten' : lang === 'fr' ? 'Reprendre' : 'Resume'}
                            >
                              <Play className="w-3.5 h-3.5" />
                            </button>
                          ) : (
                            <button
                              onClick={() => handlePauseHabit(habit.id)}
                              className="p-1 text-gray-300 hover:text-amber-500 transition-colors"
                              title={lang === 'nl' ? 'Pauzeren' : lang === 'fr' ? 'Pause' : 'Pause'}
                            >
                              <Pause className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
              <p className="text-xs text-gray-400 px-1">
                {lang === 'nl'
                  ? 'Gepauzeerde gewoontes kun je altijd weer hervatten'
                  : lang === 'fr'
                  ? 'Vous pouvez toujours reprendre les habitudes en pause'
                  : 'You can always resume paused habits'}
              </p>
            </div>
          )}
        </SettingsCard>

        {/* Reset Progress */}
        <SettingsCard>
          <button
            onClick={() => setShowResetConfirm(true)}
            className="w-full flex items-center gap-3 text-left text-red-600"
          >
            <RotateCcw className="w-5 h-5" />
            <span className="font-medium">{t('settings.resetProgress', lang)}</span>
          </button>
        </SettingsCard>

        {/* Easy Cyber Protection link */}
        <div className="pt-6">
          <a
            href="https://easycyberprotection.com"
            target="_blank"
            rel="noopener noreferrer"
            className="block bg-primary-50 rounded-xl p-4 border border-primary-100 hover:bg-primary-100 transition-colors"
          >
            <div className="flex items-center gap-3">
              <span className="text-2xl">🔒</span>
              <div className="flex-1">
                <p className="font-medium text-primary-800">
                  {t('crossSell.cyber', lang)}
                </p>
                <p className="text-sm text-primary-600 flex items-center gap-1">
                  {t('crossSell.discover', lang)}
                  <ExternalLink className="w-3 h-3" />
                </p>
              </div>
            </div>
          </a>
        </div>

        {/* Footer */}
        <div className="pt-6 text-center">
          <p className="text-xs text-gray-400">
            EasyHabits v1.0.0
          </p>
          <p className="text-xs text-gray-400 mt-1">
            {t('landing.footer', lang)}{' '}
            <a
              href="https://easycyberprotection.com"
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary-500 hover:text-primary-600"
            >
              Easy Cyber Protection
            </a>
          </p>
        </div>
      </div>

      {/* Reset Confirmation Modal */}
      {showResetConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => {
              setShowResetConfirm(false);
              setResetConfirmText('');
            }}
          />
          <div className="relative bg-white rounded-2xl w-full max-w-sm p-5 shadow-xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-red-600" />
              </div>
              <h3 className="font-semibold text-gray-900">
                {t('settings.resetProgress', lang)}
              </h3>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              {t('settings.resetWarning', lang)}
            </p>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('settings.resetConfirm', lang)}
              </label>
              <input
                type="text"
                value={resetConfirmText}
                onChange={(e) => setResetConfirmText(e.target.value.toUpperCase())}
                placeholder="RESET"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                autoComplete="off"
              />
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowResetConfirm(false);
                  setResetConfirmText('');
                }}
                className="flex-1 py-2 px-4 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium transition-colors"
              >
                {t('settings.cancel', lang)}
              </button>
              <button
                onClick={handleReset}
                disabled={resetConfirmText !== 'RESET'}
                className={`flex-1 py-2 px-4 rounded-lg font-medium transition-colors ${
                  resetConfirmText === 'RESET'
                    ? 'bg-red-600 hover:bg-red-700 text-white'
                    : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                }`}
              >
                {t('settings.resetProgress', lang)}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Template Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setShowDeleteConfirm(null)}
          />
          <div className="relative bg-white rounded-2xl w-full max-w-sm p-5 shadow-xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                <Trash2 className="w-5 h-5 text-red-600" />
              </div>
              <h3 className="font-semibold text-gray-900">
                {lang === 'nl' ? 'Gewoonte verwijderen' : lang === 'fr' ? 'Supprimer l\'habitude' : 'Delete habit'}
              </h3>
            </div>
            <div className="space-y-3 mb-6">
              <p className="text-sm text-gray-600">
                {lang === 'nl'
                  ? `Weet je zeker dat je deze gewoonte wilt verwijderen?`
                  : lang === 'fr'
                  ? `Êtes-vous sûr de vouloir supprimer cette habitude?`
                  : `Are you sure you want to delete this habit?`}
              </p>
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="text-sm text-red-700 font-medium">
                  ⚠️ {lang === 'nl'
                    ? 'Al je voortgang en geschiedenis wordt permanent verwijderd.'
                    : lang === 'fr'
                    ? 'Toute votre progression et historique sera définitivement supprimé.'
                    : 'All your progress and history will be permanently deleted.'}
                </p>
              </div>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setShowDeleteConfirm(null)}
                className="flex-1 py-2.5 px-4 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium transition-colors"
              >
                {t('settings.cancel', lang)}
              </button>
              <button
                onClick={() => {
                  if (showDeleteConfirm) {
                    removeTemplate(showDeleteConfirm.templateId);
                    setShowDeleteConfirm(null);
                  }
                }}
                className="flex-1 py-2.5 px-4 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition-colors"
              >
                {lang === 'nl' ? 'Verwijderen' : lang === 'fr' ? 'Supprimer' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Habit Edit Modal */}
      {editingHabit && (
        <HabitEditModal
          habit={editingHabit}
          customization={(progress.customHabits || {})[editingHabit.id]}
          lang={lang}
          onSave={handleSaveHabitCustomization}
          onClose={() => setEditingHabit(null)}
        />
      )}

      {/* Template Info Popup */}
      {infoTemplate && (
        <div
          className="fixed inset-0 bg-black/50 z-50 flex items-end justify-center sm:items-center p-4"
          onClick={() => setInfoTemplate(null)}
        >
          <div
            className="bg-white rounded-2xl shadow-xl w-full max-w-md max-h-[80vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="sticky top-0 bg-white border-b border-gray-100 px-4 py-3 flex items-center justify-between rounded-t-2xl">
              <div className="flex items-center gap-3">
                <span className="text-2xl">{infoTemplate.icon}</span>
                <h3 className="font-semibold text-gray-800">
                  {getLocalizedString(infoTemplate.name, lang)}
                </h3>
              </div>
              <button
                onClick={() => setInfoTemplate(null)}
                className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Content */}
            <div className="p-4 space-y-4">
              {/* Description */}
              <p className="text-gray-700">
                {getLocalizedString(infoTemplate.description, lang)}
              </p>

              {/* Info */}
              <div className="flex flex-wrap gap-3">
                <div className="flex items-center gap-2 bg-gray-100 px-3 py-1.5 rounded-lg">
                  <Clock className="w-4 h-4 text-gray-500" />
                  <span className="text-sm text-gray-700">{getLocalizedString(infoTemplate.weeklyTimeEstimate, lang)}</span>
                </div>
                <div className="flex items-center gap-2 bg-gray-100 px-3 py-1.5 rounded-lg">
                  <span className="text-sm text-gray-700">{infoTemplate.durationDays} {lang === 'nl' ? 'dagen' : lang === 'fr' ? 'jours' : 'days'}</span>
                </div>
              </div>

              {/* Habits count */}
              <p className="text-sm text-gray-500">
                {infoTemplate.habits.length} {lang === 'nl' ? 'gewoontes' : lang === 'fr' ? 'habitudes' : 'habits'}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Habit Info Popup */}
      {infoHabit && (
        <div
          className="fixed inset-0 bg-black/50 z-50 flex items-end justify-center sm:items-center p-4"
          onClick={() => setInfoHabit(null)}
        >
          <div
            className="bg-white rounded-2xl shadow-xl w-full max-w-md max-h-[80vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="sticky top-0 bg-white border-b border-gray-100 px-4 py-3 flex items-center justify-between rounded-t-2xl">
              <div className="flex items-center gap-3">
                <span className="text-2xl">{infoHabit.icon}</span>
                <h3 className="font-semibold text-gray-800">
                  {getLocalizedString(infoHabit.name, lang)}
                </h3>
              </div>
              <button
                onClick={() => setInfoHabit(null)}
                className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Content */}
            <div className="p-4 space-y-4">
              {/* Description */}
              {infoHabit.description && getLocalizedString(infoHabit.description, lang) && (
                <p className="text-gray-700">
                  {getLocalizedString(infoHabit.description, lang)}
                </p>
              )}

              {/* Schedule info */}
              {(infoHabit.targetTime || infoHabit.duration) && (
                <div className="flex flex-wrap gap-3">
                  {infoHabit.targetTime && (
                    <div className="flex items-center gap-2 bg-gray-100 px-3 py-1.5 rounded-lg">
                      <Clock className="w-4 h-4 text-gray-500" />
                      <span className="text-sm text-gray-700">{infoHabit.targetTime}</span>
                    </div>
                  )}
                  {infoHabit.duration && (
                    <div className="flex items-center gap-2 bg-gray-100 px-3 py-1.5 rounded-lg">
                      <span className="text-sm text-gray-700">{infoHabit.duration}</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function SettingsCard({ children }: { children: React.ReactNode }) {
  return (
    <div className="bg-white/80 backdrop-blur-sm rounded-xl p-4 shadow-sm">
      {children}
    </div>
  );
}
